import { _toUrlOrNull } from '@naturalcycles/js-lib'
import { ClientOnly } from '@src/components/client-only/ClientOnly.component'
import { OrphanRemover } from '@src/components/orphan-remover/OrphanRemover.component'
import { SignupButton } from '@src/components/signup-button/SignupButton.component'
import { DatoCmsMedia } from '@src/typings/dato-cms-assets'
import { type ReactNode } from 'react'
import { Helmet } from 'react-helmet-async'
import {
  PricingCompactCard,
  PricingCompactCardProps,
} from '../ab-397/pricing-compact-card/PricingCompactCard.component'
import style from './HomepageHero.module.scss'
export interface HomepageHeroProps {
  heading: string
  context: string
  cta: string
  footnote: string
  media: DatoCmsMedia | null
  bucket397?: 'test' | 'control' | null
}

export interface HomepageHeroHeadProps {
  medias: DatoCmsMedia[]
}

export function HomepageHeroHead({ medias }: HomepageHeroHeadProps): ReactNode {
  return (
    <Helmet>
      {medias.filter(Boolean).map(media => {
        const thumbnailUrl = getThumbnailUrl(media)
        if (!thumbnailUrl) return null
        return (
          <link key={`poster-preload-${media.url}`} rel="preload" as="image" href={thumbnailUrl} />
        )
      })}
    </Helmet>
  )
}

export function HomepageHero({
  media,
  heading,
  context,
  cta,
  footnote,
  bucket397,
}: HomepageHeroProps): ReactNode {
  const videoThumbnailUrl = media ? getThumbnailUrl(media) : null

  const pricingCompactCardProps: PricingCompactCardProps = {
    savings: 'Save up to 55%',
    condition: 'with NC° Annual Plan',
  }

  return (
    <section className={style.HomepageHero}>
      <div className={style.HomepageHero__overlay} />
      {media && (
        <div className={style.HomepageHero__videoContainer}>
          <video
            key={media.url}
            className={style.HomepageHero__video}
            poster={videoThumbnailUrl || undefined}
            autoPlay
            playsInline
            muted
            loop
          >
            <source src={media.url} type="video/mp4" title={media.title} />
          </video>
        </div>
      )}

      <div className={style.HomepageHero__container}>
        <h1 className={style.HomepageHero__heading}>
          <OrphanRemover textBlock={heading} />
        </h1>
        <p className={style.HomepageHero__context} dangerouslySetInnerHTML={{ __html: context }} />
        <div className={style.HomepageHero__cta}>
          <SignupButton
            utmTrackingId="home_page_hero_signup_button"
            uid="home-page-hero-signup-button"
            isWide={false}
          >
            {cta}
          </SignupButton>

          <div
            className={`${style.HomepageHero__keepYourDataSafe} ${style.HomepageHero___showDesktop}`}
          >
            <img
              alt=""
              src="/icons/fsa-badge.svg"
              className={style.HomepageHero__keepYourDataSafe__icon}
            />
            <div className={style.HomepageHero__keepYourDataSafe__text}>{footnote}</div>
          </div>
          <div className={style.HomepageHero__pricingCompactCard}>
            <ClientOnly>
              {bucket397 === 'test' && (
                <PricingCompactCard {...pricingCompactCardProps} hideOnMobile />
              )}
            </ClientOnly>
          </div>
        </div>
      </div>
    </section>
  )
}

function getThumbnailUrl(media: DatoCmsMedia): string | null {
  const url = _toUrlOrNull(media.video?.thumbnailUrl)
  if (!url) {
    return null
  }
  url.searchParams.set('time', '0')
  url.searchParams.set('width', String(300))

  return url.toString()
}
